import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, RefreshCw, Save, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

export const BackCoverBlurbModal = ({ open, onOpenChange, project, projectId, API, onProjectUpdate }) => {
  const [blurb, setBlurb] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (open && project?.back_cover_blurb) {
      setBlurb(project.back_cover_blurb);
    }
  }, [open, project?.back_cover_blurb]);

  const generateBlurb = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/generate-back-cover/${projectId}`);
      if (res.data?.blurb) {
        setBlurb(res.data.blurb);
        onProjectUpdate?.({ back_cover_blurb: res.data.blurb });
        toast.success("Back cover blurb generated!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate blurb");
    } finally {
      setGenerating(false);
    }
  };

  const saveBlurb = async () => {
    try {
      await axios.put(`${API}/project/${projectId}`, { back_cover_blurb: blurb });
      onProjectUpdate?.({ back_cover_blurb: blurb });
      toast.success("Back cover blurb saved!");
      onOpenChange(false);
    } catch {
      toast.error("Failed to save blurb");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-literary flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-rose-600" />
            Back Cover Blurb
          </DialogTitle>
          <DialogDescription>
            The back cover blurb is what readers see when they pick up your book. It should hook them and make them want to read more.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <Button
            className="w-full bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white"
            onClick={generateBlurb}
            disabled={generating}
          >
            {generating ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> {blurb ? "Regenerate Blurb" : "Generate Back Cover Blurb"}</>
            )}
          </Button>
          
          {blurb && (
            <div className="space-y-2">
              <Label>Your Back Cover Blurb</Label>
              <div className="p-4 bg-gradient-to-br from-rose-50 to-pink-50 rounded-lg border border-rose-200">
                <Textarea
                  value={blurb}
                  onChange={(e) => setBlurb(e.target.value)}
                  rows={8}
                  className="w-full bg-card/80 border-rose-200 focus:border-rose-400"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Word count: {blurb.split(/\s+/).filter(Boolean).length} words
              </p>
            </div>
          )}
          
          {blurb && (
            <div className="mt-4">
              <Label>Preview (as it would appear on the book)</Label>
              <div className="p-6 bg-slate-800 text-slate-100 rounded-lg mt-2 shadow-xl">
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-cyan-400">{project?.title || "Your Book Title"}</h3>
                  {project?.series_info?.is_series && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Book {project.series_info.book_number} of {project.series_info.total_books} in {project.series_info.series_name}
                    </p>
                  )}
                </div>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{blurb}</p>
                {project?.author_credentials && (
                  <div className="mt-4 pt-4 border-t border-slate-600">
                    <p className="text-xs text-muted-foreground italic">{project.author_credentials}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {blurb && (
            <Button onClick={saveBlurb} className="bg-rose-600 hover:bg-rose-700">
              <Save className="w-4 h-4 mr-2" /> Save Blurb
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
